import React, { useState, useEffect, useRef } from "react";
import { Input } from "antd";
import { Box, Tooltip, Grid, Typography } from "@mui/material";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import { SearchOutlined } from "@ant-design/icons";
import UploadPendingTransactions from "./uploadPendingTransactions";
import moment from "moment";
import EditTable from "./editTable";
import FiltersHeader from "../../../components/specific/transactions/filterHeader";
import OriginalEditedToggle from "../../../components/bankAccount/transactions/originalEditedToggle";
import { transactionsKindChangeHelper, needsReviewCount, getEditedTxns, getOriginalTxns, getNeedsReviewTxns } from "../../../helpers/transactions/filters";
import TransactionsCsvExport from "../../../components/bankAccount/transactions/transactionsCsvExport";
import LinearWithValueLabel from "../../../components/generic/linearProgressBar";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";

const TransactionsHome = (props) => {
  const [transactionData, setTransactionData] = useState([...props.transactionData]);
  const [showTxns, setShowTxns] = useState([...props.transactionData.filter((txn) => (props.pending || txn.is_original))]);
  const [dateRangeFilter, setDateRangeFilter] = useState([]);
  const [selectedCustomCategory, setSelectedCustomCategory] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [disableDatePicker] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState([]);
  const [generalLedgerHeader, setGeneralLedgerHeader] = useState([]);
  const [accountGrouping, setAccountGrouping] = useState(null);
  const [bankAccount, setBankAccount] = useState(null);
  const [selectGroup, setSelectGroup] = useState([]);
  const [selectBank, setSelectBank] = useState([]);
  const accountGroupingFilter = ["Bank", "Entity", "Account", "Purpose", "Tag", "Hierarchy"];
  const [selectGroupFilter, setSelectGroupFilter] = useState([]);
  const [selectBankFilter, setSelectBankFilter] = useState([]);
  const [loadingGeneralLedgerAccount, setLoadingGeneralLedgerAccount] = useState(false);
  const [loadingGeneralLedgerHeader, setLoadingGeneralLedgerHeader] = useState(false);
  const [csvDownloadAllowed, setCsvDownloadAllowed] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [csvLinkStyle, setCsvLinkStyle] = useState({
    border: 0,
    backgroundColor: "#1890ff",
    width: "100%",
    borderRadius: "5px",
    padding: "10px 20px 10px 20px",
    color: "white",
  });
  const [transactionsKind, setTransactionsKind] = useState("Original");
  const editedTxnsRef = useRef([]);
  const originalTxnsRef = useRef([]);
  const needsReviewTxnsRef = useRef([]);
  const [groupingFilters, setGroupingFilters] = useState([]);
  const [groupingOptionsCache, setGroupingOptionsCache] = useState({});
  const [loadingGroupingOptions, setLoadingGroupingOptions] = useState({ 'BA': true, 'EN': true, 'PU': true, 'AC': true, 'HI': true, 'TA': true });

  const transactionGroupingToApiMapping = {
    "Bank": "BA",
    "Entity": "EN", 
    "Purpose": "PU",
    "Account": "AC",
    "Hierarchy": "HI", 
    "Tag": "TA"
  };

  const transactionGroupingTypes = ['BA', 'EN', 'PU', 'AC', 'HI', 'TA'];

  useEffect(() => {
    transactionGroupingTypes.map((type) => {
      if (!groupingOptionsCache[type]) {
        setLoadingGroupingOptions(prev => ({ ...prev, [type]: true }));
        backendGetRequestWithCache(
          "/api/landlord/grouping-type-filter-options/",
          { params: { grouping_type: type } },
          (cachedResponse) => {
            if (cachedResponse?.data?.data && !groupingOptionsCache[type]) {
              setGroupingOptionsCache(prev => ({ ...prev, [type]: cachedResponse.data.data }));
            }
            setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
          },
          (nonCachedResponse) => {
            if (nonCachedResponse?.data?.data) {
              setGroupingOptionsCache(prev => ({ ...prev, [type]: nonCachedResponse.data.data }));
            }
            setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
          },
          (error) => {
            console.error(`Error preloading grouping options for ${type}:`, error);
            setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
          }
        );
      }
    });
  }, [groupingOptionsCache, transactionGroupingTypes]);

  useEffect(() => {
    return () => {
      setLoadingGroupingOptions({});
    };
  }, []);

  useEffect(() => {
    backendGetRequest("/api/general-ledger/account", {})
      .then((res) => {
        setGeneralLedgerAccount(res?.data?.general_ledger_account);
        setLoadingGeneralLedgerAccount(false);
      })

    backendGetRequest("/api/general-ledger/header", {})
      .then((res) => {
        setGeneralLedgerHeader(res?.data?.general_ledger_header);
        setLoadingGeneralLedgerHeader(false);
      });
  }, []);

  useEffect(() => {
    setTransactionData([...props.transactionData]);
    handleTransactionsKindChange(transactionsKind)
  }, [props.transactionData]);

  useEffect(() => {
    setTransactionData([...props.transactionData]);
    applyFilters();
  }, [props.transactionData]);

  useEffect(() => {
    let updatedCsvLinkStyle = { ...csvLinkStyle };

    if (showTxns.length > 0) {
      updatedCsvLinkStyle.cursor = 'pointer';
      setCsvDownloadAllowed(true);
    } else {
      updatedCsvLinkStyle.cursor = 'not-allowed';
      setCsvDownloadAllowed(false);
    }

    setCsvLinkStyle(updatedCsvLinkStyle);
  }, [showTxns]);


  const searchTransactions = (query, transactions) => {
    if (query === "") {
      return transactions;
    } else {
      const data = [...transactions];
      let resData = [];
      let isExclusion = false;

      if (query.startsWith("!")) {
        isExclusion = true;
        query = query.substring(1);
      }

      const searchText = query.toLowerCase();

      for (let i = 0; i < data.length; i++) {
        const bankDescription = data[i]?.info?.bank?.name ? data[i].info.bank.name.toLowerCase() : "";
        const txnDesc = data[i]?.description ? data[i].description.toLowerCase() : "";
        const transactionDate = data[i].timestamp ? data[i].timestamp.toLowerCase() : "";
        const transactionCategory = data[i].category ? data[i].category.toLowerCase() : "";
        const accountName = data[i]?.info?.account_description ? data[i].info.account_description.toLowerCase() : "";
        const amount = data[i].amount ? data[i].amount.toString().toLowerCase() : "";
        const counterPartyName = data[i].counterparty_name ? data[i].counterparty_name.toLowerCase() : "";
        // const genericTagName = data[i]?.account_generic_tags?.[0]?.name ? data[i].account_generic_tags[0].name.toLowerCase() : "";

        const matchesSearch = (
          bankDescription.includes(searchText) ||
          txnDesc.includes(searchText) ||
          transactionDate.includes(searchText) ||
          transactionCategory.includes(searchText) ||
          accountName.includes(searchText) ||
          counterPartyName.includes(searchText) ||
          amount.includes(searchText)
        );

        if ((isExclusion && !matchesSearch) || (!isExclusion && matchesSearch)) {
          resData.push(data[i]);
        }
      }
      return resData;
    }
  };

  const handleSearch = (query) => {
    const currentToggleTransactions = getOriginalOrEditedTransactions(transactionsKind);
    let resData = searchTransactions(query, currentToggleTransactions);
    if (isFilterActive) {
      resData = filterHelper(resData, dateRangeFilter, accountGrouping, selectGroup)
    }
    setSearchText(query);
    setShowTxns(resData);
  };

  useEffect(() => {
    let newSelectBankFilter = [];

    transactionData.forEach((transaction) => {
      const bank = transaction.info.bank;

      // Collect unique banks
      if (
        bank?.id !== '' &&
        !newSelectBankFilter.some((item) => item.id === bank.id)
      ) {
        newSelectBankFilter.push({
          id: bank.id,
          name: bank.name,
        });
      }
    });

    setSelectBankFilter(newSelectBankFilter);
  }, [transactionData]);



  useEffect(() => {
    if (!accountGrouping) {
      setSelectGroupFilter([]);
      return;
    }

    const apiCode = transactionGroupingToApiMapping[accountGrouping];
    if (apiCode && groupingOptionsCache[apiCode]) {
      setSelectGroupFilter(groupingOptionsCache[apiCode]);
    } else {
      setSelectGroupFilter([]);
    }
  }, [accountGrouping, groupingOptionsCache]);

  const handleDateFilter = (value) => {
    if (value?.length === 2) {
      if (value[0] === undefined) {
        value[0] = moment.min();
      }
      if (value[1] === undefined) {
        value[1] = moment.max();
      }
      setDateRangeFilter(value);
    } else {
      clearDateFilter();
    }
  };

  const handleAccountGroupingChange = (value) => {
    setAccountGrouping(value);
    setSelectGroup(null);
    setGroupingFilters([]);
  }

  const checkAccountGroup = (accountGrouping, dataItem, selectGroup) => {
    const info = dataItem.info;

    const matchers = {
      Account: () => info.bank_account_id === selectGroup,
      Category: () => dataItem.category === selectGroup,
      Purpose: () => info.bank_account_purpose_id === selectGroup,
      Entity: () => info.entity_id === selectGroup,
      Bank: () => info.bank?.id === selectGroup,
      Tag: () => dataItem.generic_tags?.some(tag => tag.id === selectGroup),
      Hierarchy: () => dataItem.account_generic_tags?.some(tag => tag.id === selectGroup),
    };

    return matchers[accountGrouping]?.() ?? false;
  };

  const filterHelper = (data, dateRangeFilter) => {
    return data.filter(txn => {
      if (dateRangeFilter.length === 2) {
        const txnDate = txn.timestamp.split("T")[0];
        const startDate = dateRangeFilter[0].format("YYYY-MM-DD");
        const endDate = dateRangeFilter[1].format("YYYY-MM-DD");
        return txnDate >= startDate && txnDate <= endDate;
      }
      return true;
    });
  };

  useEffect(() => {
    if (props.pending) {
      setShowTxns(transactionData);
    }
  }, [transactionData]);

  const applyFilters = () => {
    // Cache original/edited transactions
    editedTxnsRef.current = getEditedTxns(props.transactionData);
    originalTxnsRef.current = getOriginalTxns(props.transactionData);
    needsReviewTxnsRef.current = getNeedsReviewTxns(props.transactionData);

    // Step 1: Get filtered transactions based on toggle and search
    const currentTxns = getOriginalOrEditedTransactions(transactionsKind);
    let filteredTxns = searchTransactions(searchText, currentTxns);

    // Step 2: Date range filter
    if (dateRangeFilter.length === 2) {
      const [startDate, endDate] = dateRangeFilter.map(d => d.format("YYYY-MM-DD"));
      filteredTxns = filteredTxns.filter(txn => {
        const txnDate = txn.timestamp.split("T")[0];
        return txnDate >= startDate && txnDate <= endDate;
      });
    }

    // Step 3: Bank filter
    if (selectBank.length > 0) {
      filteredTxns = filteredTxns.filter(txn => selectBank.includes(txn.info.bank?.id));
    }

    // Step 4: Grouping filters
    if (groupingFilters.length > 0) {
      filteredTxns = filteredTxns.filter(txn =>
        groupingFilters.every(({ grouping, group, logicOperator }) => {
          if (!grouping || !group?.length) return true;

          // (if any selected group matches, include the transaction)
          const matchResults = group.map(groupId =>
            checkAccountGroup(grouping, txn, groupId)
          );

          return matchResults.some(Boolean);
        })
      );
    }

    setIsFilterActive(true);
    setShowTxns(filteredTxns);
  };


  const clearFilters = () => {
    setDateRangeFilter([]);
    setSelectGroup(null);
    setAccountGrouping(null);
    setBankAccount(null);
    setIsFilterActive(false);
    setSelectBank([]);
    const currentToggleTransactions = getOriginalOrEditedTransactions(transactionsKind);
    const resData = searchTransactions(searchText, currentToggleTransactions);
    setShowTxns(resData);
  };

  const clearDateFilter = () => {
    setDateRangeFilter([]);
  }

  const saveTxns = (row, data) => {
    const newData = [...data];
    const index = newData.findIndex((item) => row.id === item.id);
    newData.splice(index, 1, row);
    return newData;
  };

  const handleChangeLedger = (response) => {
    const data = { ...response };
    const newTransactionData = saveTxns({ ...data["record"] }, [...transactionData]);
    setTransactionData(newTransactionData);
    const newShowTxns = saveTxns({ ...data["record"] }, showTxns);
    setShowTxns(newShowTxns);
  };

  const handleTransactionsKindChange = (value) => {
    setTransactionsKind(value);
    let currentToggleTransactions = getOriginalOrEditedTransactions(value);
    let resData = searchTransactions(searchText, currentToggleTransactions)
    if (isFilterActive) {
      resData = filterHelper(resData, dateRangeFilter, accountGrouping, selectGroup);
    }
    setShowTxns(resData);
  }

  const getOriginalOrEditedTransactions = (transactionsKind) => {
    return transactionsKindChangeHelper({
      transactionsKind: transactionsKind,
      originalTransactions: originalTxnsRef.current,
      editedTransactions: editedTxnsRef.current,
      needsReviewTransactions: needsReviewTxnsRef.current,
    });
  }

  const updateSplitTransactions = (newTransactions, originalTxnId) => {
    setTransactionData((prevState) => {
      if (props.pending) {
        return [...prevState, ...newTransactions];
      }
      const updatedCompleteTxns = prevState.map((txn) => {
        if (txn.id === originalTxnId) {
          return { ...txn, is_edited: true };
        }
        return txn;
      });
      return [...updatedCompleteTxns, ...newTransactions]
    })

    setShowTxns((prevState) => {
      if (props.pending) {
        return [...prevState, ...newTransactions];
      }
      if (transactionsKind === "Edited") {
        const updatedShowCompletedTxns = prevState.filter((txn) => {
          return txn.id !== originalTxnId
        });
        const newShowTransactions = getEditedTxns(newTransactions);
        return [...updatedShowCompletedTxns, ...newShowTransactions];

      } else if (transactionsKind === "Original") {
        const updatedShowCompletedTxns = prevState.map((txn) => {
          if (txn.id === originalTxnId) {
            return { ...txn, is_edited: true };
          }
          return txn;
        });
        return updatedShowCompletedTxns;
      }
    });
  };

  return (<>
    <HeaderBox
      title={props.pending ? "Pending Transactions" : "Transactions"}
      actions={
        <Grid container alignItems="center">
          <Grid item sx={{ padding: "0px" }}>
            <Tooltip title={!csvDownloadAllowed ? "No data to export" : ""}>
              <TransactionsCsvExport pending={props.pending} transactions={showTxns} />
            </Tooltip>
          </Grid>
          {props.pending && <UploadPendingTransactions />}
        </Grid>

        //   {/* {props.generalLedgerRulePage && (
        //   <Col style={{ marginLeft: 10, marginTop: 5 }}>
        //     <TransactionsTableDropdown
        //       generalLedgerRulePage={props.generalLedgerRulePage}
        //       onAssign={props.onAssign}
        //       ruleId={props.ruleData?.id}
        //       ruleLabel={props.ruleData?.gl_tag}
        //       selectedRows={selectedRows}

        //     />
        //   </Col>
        // )}
        // {props.customTagRulePage && (
        //   <Col style={{ marginLeft: 10, marginTop: 5 }}>
        //     <TransactionsTableDropdown
        //       customTagRulePage={props.customTagRulePage}
        //       onAssign={props.onAssign}
        //       ruleId={props.ruleData?.id}
        //       ruleLabel={props.ruleData?.generic_tag}
        //       selectedRows={selectedRows}
        //     />
        //   </Col>
        // )} */}
      }
    />
    <Box width="100%" mt={1}>
      {(!props.generalLedgerRulePage && !props.customTagRulePage) && (
        <>
          {!props.pending && (
            <FiltersHeader
              dateRangeFilter={dateRangeFilter}
              disableDatePicker={disableDatePicker}
              handleDateFilter={handleDateFilter}
              clearFilters={clearFilters}
              applyFilters={applyFilters}
              setSelectGroup={setSelectGroup}
              setSelectBank={setSelectBank} // new
              selectGroup={selectGroup}
              selectBank={selectBank} // new
              accountGrouping={accountGrouping}
              bankAccount={bankAccount} // new
              handleAccountGroupingChange={handleAccountGroupingChange}
              accountGroupingFilter={accountGroupingFilter}
              selectBankFilter={selectBankFilter}
              selectGroupFilter={selectGroupFilter}
              customCategories={props.customCategories}
              selectedCustomCategory={selectedCustomCategory}
              groupingFilters={groupingFilters}
              setGroupingFilters={setGroupingFilters}
              loadingGroupingOptions={loadingGroupingOptions}
              transactionGroupingToApiMapping={transactionGroupingToApiMapping}
            />
          )}
          <Grid container spacing={2} sx={{ marginTop: "0px" }} alignItems="center">
            <Grid item xs>
              <Input
                disabled={props.loadingTable}
                placeholder="Search"
                prefix={<SearchOutlined />}
                suffix={
                  <Typography italic disabled>
                    Please enter to search
                  </Typography>
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(e.target.value);
                  }
                }}
              />
            </Grid>
            {!props.pending && (
              <Grid item>
                <OriginalEditedToggle
                  handleTransactionsKindChange={handleTransactionsKindChange}
                  transactionsKind={transactionsKind}
                  needsReviewCount={needsReviewCount(transactionData)}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
      <Box sx={{ width: '100%', padding: "10px 0px" }}>
        <LinearWithValueLabel numerator={props.progressNumerator} denominator={props.progressDenominator} />
      </Box>
      <EditTable
        data={showTxns}
        pending={props.pending}
        ruleData={props.ruleData}
        AccountLedgerData={props.generalLedgerAccount}
        HeaderLedgerData={props.generalLedgerHeader}
        onChange={handleChangeLedger}
        customCategories={props.customCategories}
        loading={props.loadingTable}
        updateTransactions={props.updateTransactions}
        updateTransaction={props.updateTransaction}
        updateSplitTransactions={updateSplitTransactions}
        onLoadTransactions={props.onLoadTransactions}
        transactionsLoading={props.transactionsLoading}
        generalLedgerRulePage={props.generalLedgerRulePage}
        customTagRulePage={props.customTagRulePage}
        currentMonth={props.currentMonth}
        currentYear={props.currentYear}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    </Box>
  </>
  );
}

export default TransactionsHome;
